import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../components/utils/api';
import { OffMarketWillingnessEvent as OffMarketWillingnessEventType } from '../components/utils/types';
import ListingCard from '../components/ListingCard';
import MyListingCard from '../components/ListingCard/MyListingCard';
import { listingShareUrl } from '../components/utils/listingSlug';
import { useOpenListing } from '../components/utils/useOpenListing';
import { shareUrlWithFallback } from '../utils/shareUtils';
import { ListingCardListing } from '../components/ListingCard/types';
import ProfileSubviewHeader from '../components/ProfileView/ProfileSubviewHeader';
import {
  AreaInterest,
  deleteAreaInterest,
  getMyAreaInterests,
} from '../components/AppContent/NewLandingPage/CouldBuyFlow/areaInterestApi';
import {
  areaInterestToCardListing,
  criteriaPriceLabel,
} from '../components/AppContent/NewLandingPage/CouldBuyFlow/areaInterestCard';
import './MyListings.scss';

interface MyListingsProps {
  listingType: 'sell' | 'buy';
  onBack?: () => void;
  /** The profile view draws the header itself; a page mounting this standalone keeps its own. */
  hideHeader?: boolean;
  /** Buy mode: opens CouldBuyFlow pre-filled to edit an area interest. */
  onEditAreaInterest?: (interest: AreaInterest) => void;
}

/**
 * One entry of the merged Buy-interests list: a building-level could-buy or an
 * area interest, carrying a shared timestamp so both kinds sort by recency.
 */
type BuyRow =
  | { kind: 'listing'; ts: number; listing: OffMarketWillingnessEventType }
  | { kind: 'area'; ts: number; interest: AreaInterest };

/** Epoch millis of an ISO date string; missing/invalid dates sort oldest. */
const rowTimestamp = (iso: string | undefined): number => {
  const ts = iso ? Date.parse(iso) : NaN;
  return Number.isFinite(ts) ? ts : 0;
};

/**
 * A circle-only interest whose anchor address is unknown would render as a bare
 * "500 m radius" — a card that identifies nothing. Hidden from this list;
 * still editable/deletable via the area-interests deep link.
 */
const hasIdentifiablePlace = (interest: AreaInterest): boolean =>
  interest.display_areas.some((a) => a.kind !== 'circle') ||
  !!interest.anchor_address?.street;

// Exported for the regression test: the /api/user/*-listings response already
// carries the Jaakko estimate fields, and this reshape must preserve them or
// the profile cards silently fall back to "No set price".
export function mapListing(
  listing: any,
  type: 'sell' | 'buy',
): OffMarketWillingnessEventType {
  return {
    _id: listing._id,
    buildingId: listing.building_id,
    isUserOwnEvent: true,
    could_sell: type === 'sell',
    could_buy: type === 'buy',
    last_modified_value: type === 'sell' ? 'COULD_SELL' : 'COULD_BUY',
    property_description: listing.property_description,
    criteria: listing.criteria,
    // Digest notify flags: dropped here and the buy-edit form would prefill its
    // defaults, then save them over the buyer's stored choice.
    notify_email: listing.notify_email,
    notify_sms: listing.notify_sms,
    property_size: listing.property_size,
    property_size_max: listing.property_size_max,
    property_rooms: listing.property_rooms,
    property_rooms_flexible: listing.property_rooms_flexible,
    property_floor: listing.property_floor,
    property_has_elevator: listing.property_has_elevator,
    property_has_balcony: listing.property_has_balcony,
    property_renovation_status: listing.property_renovation_status,
    property_asking_price: listing.property_asking_price,
    estimate_status: listing.estimate_status,
    estimated_price: listing.estimated_price,
    estimated_price_range: listing.estimated_price_range,
    estimated_price_currency: listing.estimated_price_currency,
    address: {
      street: listing.address?.street || '',
      houseNumber: listing.address?.houseNumber || '',
      city: listing.address?.city || '',
      country: listing.address?.country || '',
      region: listing.address?.region || '',
      postcode: listing.address?.postcode || '',
      coordinates: listing.address?.coordinates || { lat: 0, lng: 0 },
    },
    images: listing.images,
    updated_at: listing.updated_at,
    verified: listing.verified,
  };
}

/**
 * The owner's sell or buy listings page with the manage menu. Rows being
 * re-estimated show as estimating until the server agrees or the start does
 * not happen.
 */
const MyListings: React.FC<MyListingsProps> = ({
  listingType,
  onBack,
  hideHeader,
  onEditAreaInterest,
}) => {
  const { t } = useTranslation();
  const openListing = useOpenListing();
  const [listings, setListings] = useState<OffMarketWillingnessEventType[]>([]);
  const [areaInterests, setAreaInterests] = useState<AreaInterest[]>([]);
  // Area interest awaiting delete confirmation, and its in-flight flag.
  const [areaPendingDelete, setAreaPendingDelete] = useState<string | null>(null);
  const [deletingArea, setDeletingArea] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 5;

  const isSell = listingType === 'sell';
  const endpoint = isSell
    ? '/api/user/sell-listings'
    : '/api/user/buy-listings';
  const title = isSell
    ? t('profile.mySellListings')
    : t('profile.myBuyInterests');
  const emptyText = isSell
    ? t('profile.noSellListings')
    : t('profile.noBuyListings');

  // Buy interests are one list regardless of shape: building-level could-buys
  // and area interests, merged and sorted newest-first. Sell mode has no areas,
  // so its rows pass through unchanged.
  const rows: BuyRow[] = [
    ...listings.map((listing): BuyRow => ({
      kind: 'listing',
      ts: rowTimestamp(listing.updated_at),
      listing,
    })),
    ...areaInterests.filter(hasIdentifiablePlace).map((interest): BuyRow => ({
      kind: 'area',
      ts: rowTimestamp(interest.created_at),
      interest,
    })),
  ].sort((a, b) => b.ts - a.ts);

  const totalPages = Math.ceil(rows.length / ITEMS_PER_PAGE);
  const paginatedRows = rows.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  // Ids whose re-estimate has been commissioned but not yet acknowledged by the
  // server. A ref, not state: the save triggers a refetch that would otherwise
  // paint the old price straight back over the optimistic row.
  const estimatingIdsRef = useRef<Set<string>>(new Set());

  /**
   * Show a row as estimating while its re-estimate is still being registered.
   * The id is dropped as soon as the server agrees the estimate is pending, or
   * when the edit reports `jaakko:listing-estimate-not-started`, so this only
   * ever covers the gap and a refused start does not leave the row stuck.
   */
  const applyOptimisticEstimating = (
    listing: OffMarketWillingnessEventType,
  ): OffMarketWillingnessEventType => {
    if (!estimatingIdsRef.current.has(listing._id)) return listing;
    if (listing.estimate_status === 'pending') {
      estimatingIdsRef.current.delete(listing._id);
      return listing;
    }
    return { ...listing, estimate_status: 'pending' };
  };

  const fetchListings = useCallback(async () => {
    try {
      const listingsPromise = apiRequest(endpoint);
      // Both requests go out together, but the areas one is only awaited after
      // the listings await below — so its rejection needs a handler now, or a
      // listings failure jumps to the catch and leaves it unhandled. Areas are
      // not fatal to this page: the list just renders without them. The error
      // still propagates out of getMyAreaInterests for any caller that wants
      // to surface it.
      const areasPromise: Promise<AreaInterest[]> = isSell
        ? Promise.resolve([])
        : getMyAreaInterests().catch(() => []);
      const res = await listingsPromise;
      setListings(
        (res.listings || [])
          .map((l: any) => mapListing(l, listingType))
          .map(applyOptimisticEstimating),
      );
      setAreaInterests(await areasPromise);
    } catch {
      // silently handle
    } finally {
      setLoading(false);
    }
  }, [endpoint, listingType, isSell]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  // Re-pull the list on the global "listings changed" signal (JAA-304): editing
  // a buy listing happens in CouldBuyFlow, which lives outside this page and
  // only reaches back via this event — without it, a saved edit wouldn't show
  // here until the next full remount.
  useEffect(() => {
    window.addEventListener('jaakko:listings-changed', fetchListings);
    return () => window.removeEventListener('jaakko:listings-changed', fetchListings);
  }, [fetchListings]);

  // An edit that changes what the place is commissions a new estimate, but the
  // listing is only stamped `pending` once the estimation service has accepted
  // the job — a round trip the user should not have to watch a stale price
  // through. These ids render as estimating from the moment the edit is saved.
  // Held in a ref because the save also triggers a refetch, which would
  // otherwise paint the old price straight back over the optimistic state.
  useEffect(() => {
    const onEstimating = (e: Event) => {
      const { willingnessId } = (e as CustomEvent).detail as { willingnessId: string };
      estimatingIdsRef.current.add(willingnessId);
      setListings((prev) => prev.map(applyOptimisticEstimating));
    };
    window.addEventListener('jaakko:listing-estimating', onEstimating);
    return () => window.removeEventListener('jaakko:listing-estimating', onEstimating);
  }, []);

  useEffect(() => {
    /** Drops a refused or failed re-estimate's optimistic row and refetches the server copy it replaced. */
    const onNotStarted = (e: Event) => {
      const { willingnessId } = (e as CustomEvent).detail as {
        willingnessId: string;
      };
      estimatingIdsRef.current.delete(willingnessId);
      fetchListings();
    };
    window.addEventListener(
      'jaakko:listing-estimate-not-started',
      onNotStarted,
    );
    return () =>
      window.removeEventListener(
        'jaakko:listing-estimate-not-started',
        onNotStarted,
      );
  }, [fetchListings]);

  // Deleting an area interest is irreversible, so it asks first — the same
  // dialog the building-level listing delete uses, rather than removing the row
  // the moment the menu item is clicked.
  const handleDeleteAreaInterest = useCallback(
    async (id: string) => {
      setDeletingArea(true);
      try {
        await deleteAreaInterest(id);
        setAreaInterests((prev) => prev.filter((a) => a.area_interest_id !== id));
        setAreaPendingDelete(null);
      } catch {
        fetchListings();
        setAreaPendingDelete(null);
      } finally {
        setDeletingArea(false);
      }
    },
    [fetchListings],
  );

  // Share this listing via the backend share URL (OG preview + redirect to the
  // canonical route).
  const shareListing = (listing: OffMarketWillingnessEventType) => {
    shareUrlWithFallback({ url: listingShareUrl(listing._id), t });
  };

  const renderRow = (row: BuyRow) =>
    row.kind === 'listing' ? (
      // Both sides are owner rows, so both get the manage menu — sell from
      // JAA-303, buy from JAA-304. building_id is mapped off buildingId because
      // edit resolves the listing's building from it.
      <MyListingCard
        key={row.listing._id}
        type={isSell ? 'seller' : 'buyer'}
        listing={
          {
            ...(row.listing as unknown as ListingCardListing),
            building_id: row.listing.buildingId,
          } as ListingCardListing
        }
        onOpen={() =>
          openListing(row.listing as unknown as ListingCardListing)
        }
        onChanged={fetchListings}
      />
    ) : (
      <ListingCard
        key={row.interest.area_interest_id}
        size="small"
        type="buyer"
        listing={
          areaInterestToCardListing(row.interest, t) as unknown as ListingCardListing
        }
        priceText={criteriaPriceLabel(row.interest) || undefined}
        priceTextShort={criteriaPriceLabel(row.interest, true) || undefined}
        onOpen={openListing}
        onEdit={() => onEditAreaInterest?.(row.interest)}
        onDelete={() => setAreaPendingDelete(row.interest.area_interest_id)}
        isLoggedIn
      />
    );

  return (
    <div className="my-listings">
      {onBack && !hideHeader && <ProfileSubviewHeader title={title} onBack={onBack} />}

      {loading ? (
        <div className="my-listings__spinner">
          <div className="my-listings__spinner-circle" />
        </div>
      ) : (
        <div className="my-listings__section">
          {rows.length > 0 ? (
            paginatedRows.map(renderRow)
          ) : (
            <p className="my-listings__empty">{emptyText}</p>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="my-listings__pagination">
          <button
            className="my-listings__pagination-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            {t('common.previous')}
          </button>
          <div className="my-listings__pagination-info">
            <span>{t('priceEstimations.pageInfo')}</span>
            <span>
              {page} / {totalPages}
            </span>
          </div>
          <button
            className="my-listings__pagination-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Portalled to <body> and styled with the shared confirm classes so the
          area-interest delete asks in exactly the same words and shape as the
          building-level one. */}
      {areaPendingDelete &&
        ReactDOM.createPortal(
          <div
            className="end-chat-confirmation-overlay"
            onClick={() => !deletingArea && setAreaPendingDelete(null)}
          >
            <div
              className="end-chat-confirmation"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="end-chat-confirmation__header">
                <h3>{t('building.offMarket.modal.deleteConfirmTitle', 'Remove listing')}</h3>
              </div>
              <div className="end-chat-confirmation__content">
                <p>
                  {t(
                    'building.offMarket.modal.deleteConfirmText',
                    'Are you sure you want to remove this event?',
                  )}
                </p>
              </div>
              <div className="end-chat-confirmation__actions">
                <button
                  className="end-chat-confirmation__button end-chat-confirmation__button--cancel"
                  onClick={() => setAreaPendingDelete(null)}
                  disabled={deletingArea}
                >
                  {t('building.offMarket.modal.cancel', 'Cancel')}
                </button>
                <button
                  className="end-chat-confirmation__button end-chat-confirmation__button--confirm"
                  onClick={() => handleDeleteAreaInterest(areaPendingDelete)}
                  disabled={deletingArea}
                >
                  {deletingArea
                    ? t('building.offMarket.modal.loading', 'Loading...')
                    : t('building.offMarket.modal.delete', 'Remove')}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default MyListings;
